import { useEffect, useState } from 'react';

import {
  failConnectionAddOperation,
  failConnectionReconnectOperation,
  startConnectionAddOperation,
  startConnectionReconnectOperation,
} from '@mint-lib/connections-ui';
import { useTranslation } from '@mint-lib/i18n';
import type {
  AuthorizeResult,
  NormalizedConnection,
  OAuthFlow,
} from '@mint-lib/integration-registry';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  GoogleAnalyticsIntegrations,
  PlatformUrns,
} from '@mint-sdk/api-integrations';
import { LocalStorage } from '@mint-sdk/api-omni-trafficking';

const GA4_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.googleAnalytics;

/**
 * GA4's connect/reconnect redirect flow. Ported from packages/connections'
 * `ga4AuthorizeStrategy` (traffickingReconnectStrategies.ts) and
 * `useCreateOAuthConnectionHandlers`: fetches a Google authorization URL,
 * stashes the oauthFlowId/connectionName/connectionId the callback will need
 * in localStorage (read back by the add-form via the same
 * `LocalStorage.get*From` helpers), then redirects the browser to Google.
 *
 * With `ga4BackendOwnedAuth` on, it instead POSTs the connection name (new)
 * or id (reconnect) and just redirects: the backend owns the callback.
 *
 * Used for both the add-form's initial "Connect account" action (no
 * `connection` yet) and the manage-form's "Reconnect" action (existing
 * `connection`).
 */
function useAuthorizeGA4({
  connection,
}: {
  connection?: NormalizedConnection;
}): AuthorizeResult {
  const { t } = useTranslation('@mint-lib/ga4-integration');
  const ff = useFlags('@mint/arm', ['ga4BackendOwnedAuth']);
  const isBackendOwnedAuth = ff.ga4BackendOwnedAuth.enabled;
  const [enabled, setEnabled] = useState(false);
  const connectionId = connection?.id || undefined;
  const connectionName = connection?.name ?? '';

  // Backend-owned flow: the backend stores the OAuth flow itself and
  // redirects straight to the Update route with `oauth_result`, so there's
  // nothing to stash in localStorage.
  const {
    mutate: requestAuthorizationUrl,
    data: authorizationUrlData,
    error: authorizationUrlError,
    isPending: isAuthorizationUrlPending,
  } = GoogleAnalyticsIntegrations.getGA4OauthUrl();

  // TODO(ga4BackendOwnedAuth): delete the legacy (frontend-owned) branch
  // below, the `*Legacy` endpoints and the localStorage handoff along with
  // the flag.
  const {
    data: legacyData,
    error: legacyError,
    refetch,
    isLoading: isLegacyLoading,
  } = useQuery({
    ...GoogleAnalyticsIntegrations.getGA4OauthUrlLegacy({
      params: { integrationId: connectionId || null },
    }),
    // Deviation from the legacy `ga4AuthorizeStrategy`, which also gated
    // this query on `!connectionId` — that would make reconnecting an
    // existing connection a no-op, which can't be right now that this single
    // `useAuthorize` must serve both the add-form and the manage-form.
    enabled: enabled && !isBackendOwnedAuth,
  });

  useEffect(() => {
    if (isBackendOwnedAuth || !legacyData?.oauthFlowId) return;
    LocalStorage.setGA4OauthFlow(
      connectionName,
      legacyData.oauthFlowId,
      connectionId,
    );
  }, [
    isBackendOwnedAuth,
    legacyData?.oauthFlowId,
    connectionName,
    connectionId,
  ]);

  const authorizationUrl = isBackendOwnedAuth
    ? authorizationUrlData?.authorizationUrl
    : legacyData?.authorizationUrl;
  const error = isBackendOwnedAuth ? authorizationUrlError : legacyError;

  useEffect(() => {
    if (authorizationUrl) {
      globalThis.location.assign(authorizationUrl);
    }
  }, [authorizationUrl]);

  useEffect(() => {
    if (!error) return;
    failConnectionAddOperation('error');
    failConnectionReconnectOperation('error');
    enqueueSnackbar({
      variant: 'error',
      title: t('Error'),
      subTitle: t('Something went wrong. Please try again later.'),
    });
  }, [error, t]);

  const startConnectionOperation = () => {
    // The add form passes a draft connection with an empty id (it only
    // carries the typed name); a real id means a reconnect.
    const context = {
      platform: GA4_URN,
      flow: 'oauth',
      registry: true,
    } as const;
    if (connectionId) {
      startConnectionReconnectOperation(context);
    } else {
      startConnectionAddOperation(context);
    }
  };

  if (isBackendOwnedAuth) {
    return {
      authorize: () => {
        startConnectionOperation();
        requestAuthorizationUrl({
          data: connectionId
            ? { integrationId: connectionId }
            : { name: connectionName },
        });
      },
      isPending: isAuthorizationUrlPending,
    };
  }

  return {
    authorize: () => {
      startConnectionOperation();
      LocalStorage.resetGA4OauthFlow();
      setEnabled(true);
      void refetch();
    },
    isPending: isLegacyLoading,
  };
}

export function createGA4OAuthFlow(): OAuthFlow {
  return {
    // Ported from `ga4OauthStrategy.isEnabled` — GA4's callback URL only
    // ever carries `state` + `code` (no platform-specific marker to check,
    // unlike AdForm's encoded state payload).
    canProcess: (search) => Boolean(search.state) && Boolean(search.code),
    useAuthorize: useAuthorizeGA4,
  };
}
